import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const RealTimeStatusBoard = ({ batches, onRefresh }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCase, setFilterCase] = useState('all');
  const [uniqueCases, setUniqueCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Fetch unique cases
  useEffect(() => {
    const fetchCases = async () => {
      if (!batches || batches.length === 0) {
        setUniqueCases([]);
        return;
      }

      try {
        setLoadingCases(true);
        
        // Get unique case_ids from batches
        const caseIds = [...new Set(batches.map(batch => batch?.case_id).filter(Boolean))];
        
        if (caseIds.length === 0) {
          setUniqueCases([]);
          setLoadingCases(false);
          return;
        }

        // Fetch case details from Supabase
        const { data: casesData, error } = await supabase
          .from('cases')
          .select('id, case_number, patient:patients(id, name)')
          .in('id', caseIds)
          .order('case_number', { ascending: false });

        if (error) throw error;

        const casesMap = casesData?.map(caseItem => ({
          id: caseItem.id,
          caseNumber: caseItem.case_number,
          patientName: caseItem.patient?.name || 'Patient inconnu',
          batchCount: batches.filter(b => b.case_id === caseItem.id).length
        })) || [];

        setUniqueCases(casesMap);
      } catch (error) {
        console.error('Error fetching cases:', error);
        setUniqueCases([]);
      } finally {
        setLoadingCases(false);
      }
    };

    fetchCases();
  }, [batches]);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'En attente', color: 'bg-gray-100 text-gray-700', icon: 'Clock', progress: 0 },
      in_progress: { label: 'En cours', color: 'bg-primary/10 text-primary', icon: 'Play', progress: 50 },
      quality_check: { label: 'Contrôle qualité', color: 'bg-warning/10 text-warning', icon: 'CheckCircle', progress: 75 },
      completed: { label: 'Terminé', color: 'bg-success/10 text-success', icon: 'Check', progress: 100 },
      delayed: { label: 'Retardé', color: 'bg-error/10 text-error', icon: 'AlertCircle', progress: 30 }
    };
    return configs?.[status] || configs?.pending;
  };

  // Get case info for a batch
  const getCaseInfo = (batch) => {
    if (!batch?.case_id) return null;
    return uniqueCases.find(c => c.id === batch.case_id);
  };

  // Filter batches by status and case
  const filteredBatches = batches?.filter(batch => {
    const statusMatch = filterStatus === 'all' || batch?.status === filterStatus;
    const caseMatch = filterCase === 'all' || batch?.case_id === filterCase;
    return statusMatch && caseMatch;
  }) || [];

  const getQRScanHistory = (batch) => {
    // Mock QR scan history - in real implementation, fetch from database
    return [
      { phase: 'Réception', operator: 'OP-001', timestamp: new Date(batch?.startDate || batch?.start_date) },
      { phase: 'Fabrication', operator: 'OP-003', timestamp: new Date(batch?.updatedAt || batch?.updated_at) }
    ];
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par statut
          </label>
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'in_progress', 'quality_check', 'completed', 'delayed']?.map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'Tous' : getStatusConfig(status)?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Case Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par cas
          </label>
          {loadingCases ? (
            <div className="text-sm text-gray-500">Chargement des cas...</div>
          ) : (
            <select
              value={filterCase}
              onChange={(e) => setFilterCase(e.target.value)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Tous les cas ({batches?.length || 0} lots)</option>
              {uniqueCases.map(caseItem => (
                <option key={caseItem.id} value={caseItem.id}>
                  {caseItem.caseNumber} - {caseItem.patientName} ({caseItem.batchCount} lot{caseItem.batchCount > 1 ? 's' : ''})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Active Filters Summary */}
        {(filterStatus !== 'all' || filterCase !== 'all') && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtres actifs:</span>
            {filterStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                {getStatusConfig(filterStatus)?.label}
                <button
                  onClick={() => setFilterStatus('all')}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filterCase !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">
                {uniqueCases.find(c => c.id === filterCase)?.caseNumber}
                <button
                  onClick={() => setFilterCase('all')}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setFilterStatus('all');
                setFilterCase('all');
              }}
              className="text-xs text-gray-600 hover:text-gray-900 ml-2"
            >
              Réinitialiser tout
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{filteredBatches?.length}</span> lot(s) affiché(s)
        </p>
        <button
          onClick={onRefresh}
          className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
        >
          <Icon name="RefreshCw" size={14} />
          Actualiser
        </button>
      </div>

      {/* Status Board */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredBatches?.map(batch => {
          const statusConfig = getStatusConfig(batch?.status);
          const scanHistory = getQRScanHistory(batch);
          const caseInfo = getCaseInfo(batch);

          return (
            <div key={batch?.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="QrCode" size={20} className="text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {batch?.batchNumber || batch?.batch_number}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    {batch?.patientName || batch?.patient_name}
                  </p>
                  
                  {/* Case Number */}
                  {caseInfo && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
                        <Icon name="FolderOpen" size={12} />
                        {caseInfo.caseNumber}
                      </span>
                    </div>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusConfig?.color}`}>
                  <Icon name={statusConfig?.icon} size={14} />
                  {statusConfig?.label}
                </span>
              </div>

              {/* Current Phase Badge */}
              {batch?.current_phase && (
                <div className="mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                    <Icon name="Layers" size={12} />
                    Phase {batch.current_phase}
                  </span>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Progression</span>
                  <span className="font-semibold text-gray-900">{batch?.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all duration-300"
                    style={{ width: `${batch?.progress || 0}%` }}
                  />
                </div>
              </div>

              {/* QR Scan History */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Historique Scans QR</h4>
                <div className="space-y-2">
                  {scanHistory?.map((scan, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{scan?.phase}</span>
                        <span className="text-gray-600 ml-2">par {scan?.operator}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {scan?.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Machine Assignment */}
              {batch?.machine && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Settings" size={16} className="text-gray-400" />
                    <span className="text-gray-600">Machine:</span>
                    <span className="font-medium text-gray-900">
                      {batch?.machine?.machine_name}
                    </span>
                  </div>
                </div>
              )}

              {/* Deadline */}
              {batch?.deadline && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Icon name="Calendar" size={16} className="text-gray-400" />
                  <span className="text-gray-600">Échéance:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(batch.deadline).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredBatches?.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Icon name="Package" size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun lot trouvé</h3>
          <p className="text-sm text-gray-600 mb-4">
            Aucun lot ne correspond aux filtres sélectionnés
          </p>
          <button
            onClick={() => {
              setFilterStatus('all');
              setFilterCase('all');
            }}
            className="text-sm text-primary hover:underline"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}
    </div>
  );
};

export default RealTimeStatusBoard;